/**
 * ActivityFeed — connection-scoped feed (Phase D, AC-22). Invites and presence
 * are actionable in place. Blocked and invisible actors are excluded
 * server-side, so this component renders whatever it is given.
 */

import { useActivity } from '../../context/ActivityContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { InviteCard } from './InviteCard';
import { PresenceBadge } from './PresenceBadge';
import type { ActivityEntry } from '../../types/social';
import './ActivityFeed.css';

interface Props {
  onEnterRoom?: (roomId: string) => void;
  onMatch?: (matchId: string) => void;
}

function describe(entry: ActivityEntry): string {
  switch (entry.kind) {
    case 'connection_accepted':
      return 'is now connected with you';
    case 'came_online':
      return 'came online';
    case 'entered_game':
      return 'entered a game';
    case 'presence':
      return 'updated their status';
    case 'invite':
      return 'sent you an invite';
    default:
      return '';
  }
}

export function ActivityFeed({ onEnterRoom, onMatch }: Props) {
  const enabled = useFeatureFlag('social.invites_feed');
  const { feed, isLoading } = useActivity();

  if (!enabled) return null;
  if (isLoading && feed.length === 0) {
    return <p className="activity-feed__empty">Loading activity…</p>;
  }
  if (feed.length === 0) {
    return <p className="activity-feed__empty">Nothing new from your connections.</p>;
  }

  return (
    <ul className="activity-feed">
      {feed.map((entry) => (
        <li className="activity-feed__row" key={entry.id}>
          {entry.kind === 'invite' && entry.invite ? (
            <InviteCard invite={entry.invite} onEnterRoom={onEnterRoom} onMatch={onMatch} />
          ) : (
            <div className="activity-feed__event">
              <div
                className="activity-feed__avatar"
                style={
                  entry.actorAvatarUrl
                    ? { backgroundImage: `url(${entry.actorAvatarUrl})` }
                    : undefined
                }
              />
              <div className="activity-feed__text">
                <span className="activity-feed__actor">{entry.actorDisplayName}</span>{' '}
                <span className="activity-feed__desc">{describe(entry)}</span>
              </div>
              {entry.presence && <PresenceBadge presence={entry.presence} />}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export default ActivityFeed;
